import { SignedIn, UserButton } from '@clerk/clerk-react';
import {
    Outlet,
    createRootRouteWithContext,
    redirect,
    useMatches,
    useNavigate,
} from '@tanstack/react-router';
import { Toaster } from 'sonner';
import { useTranslation } from 'react-i18next';

import { useHtmlLang } from '@/i18n/use-html-lang';

import { ALargeSmall, Settings } from '@/assets/icons';
import { AppNav } from '@/components/app-nav';
import { ConsentBanner } from '@/components/consent/consent-banner';
import { ThemeController } from '@/components/theme-controller';
import { UnproofreadTranslationNotice } from '@/components/unproofread-translation-notice';
import { useEffectiveTheme } from '@/hooks/use-effective-theme';
import { useIsMobile } from '@/hooks/use-is-mobile';
import { isPublicRoute } from '@/config/public-routes';
import { signInRedirectFor } from '@/config/sign-in-redirect';
import type { ClerkAuthContext } from '@/index';

export interface RouterContext {
    auth: ClerkAuthContext;
}

export const Route = createRootRouteWithContext<RouterContext>()({
    beforeLoad: ({ context, location }) => {
        // Enforce the sign-in guard everywhere, including the Vite dev server,
        // so local development exercises the real Clerk auth flow (and the
        // signed-in-only UI such as the UserButton/AppNav renders locally).
        if (isPublicRoute(location.pathname)) return;
        if (!context.auth.isSignedIn) {
            const redirectUrl = signInRedirectFor(location);
            throw redirect({
                to: '/sign-in/$',
                params: { _splat: '' },
                search: redirectUrl === undefined ? {} : { redirect_url: redirectUrl },
            });
        }
    },
    component: RootComponent,
});

function RootComponent() {
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    useHtmlLang(i18n.language);
    // Chromeless routes (the diagram embed iframe) suppress the app chrome:
    // the embed is a bare mini canvas inside a modal, and AppNav/UserButton
    // would float over it. Opt in via the route's staticData.
    const matches = useMatches();
    const chromeless = matches.some(
        (m) => (m.staticData as { chromeless?: boolean } | undefined)?.chromeless === true,
    );
    // ownTopRight routes render the account corner inside their properties
    // column or header tray (Figma-style panel-owns-the-corner); suppress
    // only the fixed top-right UserButton there.
    const ownTopRight = matches.some(
        (m) => (m.staticData as { ownTopRight?: boolean } | undefined)?.ownTopRight === true,
    );
    // ownTopLeft routes render the app nav inside their page-level
    // EditorHeaderTray. That tray is desktop-only, so the suppression is too
    // — on mobile the fixed corner button stays, or the editor would have no
    // nav at all.
    const ownTopLeft = matches.some(
        (m) => (m.staticData as { ownTopLeft?: boolean } | undefined)?.ownTopLeft === true,
    );
    const isMobile = useIsMobile();
    const theme = useEffectiveTheme();
    return (
        <div className="min-h-screen w-full">
            {!chromeless && !(ownTopLeft && !isMobile) && (
                <SignedIn>
                    <div className="pointer-events-auto fixed top-4 left-4 z-50">
                        <AppNav />
                    </div>
                </SignedIn>
            )}
            {!chromeless && !ownTopRight && (
                <SignedIn>
                    <div className="pointer-events-auto fixed top-4 right-4 z-50">
                        {/* Menu items are kept in sync with the properties column's own
                            UserButton in ColumnCornerControls (src/components/properties-column/column-corner-controls.tsx). */}
                        <UserButton afterSignOutUrl="/sign-in">
                            <UserButton.MenuItems>
                                <UserButton.Action
                                    label={t('settingsPage.title')}
                                    labelIcon={<Settings className="size-4" />}
                                    onClick={() => void navigate({ to: '/settings', search: { section: 'appearance' } })}
                                />
                                <UserButton.Action
                                    label={t('settings.fontSize.label')}
                                    labelIcon={<ALargeSmall className="size-4" />}
                                    onClick={() => void navigate({ to: '/settings', search: { section: 'appearance' } })}
                                />
                            </UserButton.MenuItems>
                        </UserButton>
                    </div>
                </SignedIn>
            )}
            {!chromeless && <ThemeController />}
            <Outlet />
            {!chromeless && <UnproofreadTranslationNotice language={i18n.language} />}
            {!chromeless && <ConsentBanner />}
            <Toaster richColors position="bottom-right" theme={theme} />
        </div>
    );
}
